import { formatDistanceToNow } from 'date-fns';
import type { CredentialRejectionSurface } from '@erp/shared-types';
import { msg, useMessages } from 'gt-react';

/**
 * Vocabulary shared by every surface that explains an operator-reported credential rejection —
 * the org settings resource list, the per-agent resources tab and the credentials list. Kept apart
 * from the components in `CredentialRejectionNotice.tsx` so that file exports components only and
 * Fast Refresh can preserve their state.
 */

/**
 * The human name for the sign-in the credential was refused at. Deliberately lower-case: every use
 * is mid-sentence ("rejected at the portal sign-in").
 */
const SURFACE_LABEL_MSGS: Record<CredentialRejectionSurface, string> = {
  portal: msg('portal', {
    context:
      'where a credential was rejected: the Azure/Entra web sign-in page. Used mid-sentence, e.g. "rejected at the portal sign-in"',
  }),
  vm: msg('VM', {
    context:
      'where a credential was rejected: the password prompt inside the remote desktop session. Used mid-sentence',
  }),
  application: msg('application', {
    context:
      'where a credential was rejected: an application running inside the remote desktop. Used mid-sentence',
  }),
};

/** Translate a `CredentialRejectionSurface` into its mid-sentence label. */
export function useCredentialSurfaceLabel(): (
  surface: CredentialRejectionSurface,
) => string {
  const m = useMessages();
  return (surface) => m(SURFACE_LABEL_MSGS[surface]);
}

/** Relative wording ("2 hours ago"), matching how the rest of the app ages a timestamp. */
export function rejectionTimeAgo(isoInstant: string): string {
  return formatDistanceToNow(new Date(isoInstant), { addSuffix: true });
}
